import QrScanner from "qr-scanner";
import { useEffect, useRef, useCallback } from "react";
import localForage from "localforage";
import { FREYA_DEVICE_ID_KEY } from "./FreyaPrintSettings";
import { useInitCameraDevices } from "./useInitCameraDevices";

interface Props {
  videoRef?: HTMLVideoElement;
  onScan?: (result: QrScanner.ScanResult) => Promise<void>;
  useIndexDB?: boolean;
  deviceStorageKey?: string;
  ignoreSameAsLastQr?: boolean;
}

export const useCameraScanner = ({ videoRef, onScan, useIndexDB = false, deviceStorageKey, ignoreSameAsLastQr = false }: Props) => {
  const { cameraDevices, selectedCameraDevice, qrScanner, setCamera, initCameraDevices } = useInitCameraDevices(useIndexDB);
  const lastScannedCodeRef = useRef<string | null>(null);
  const mountedRef = useRef(true);

  const handleScan = async (result: QrScanner.ScanResult) => {
    if (!mountedRef.current) return;
    if (result.data === lastScannedCodeRef.current && ignoreSameAsLastQr) {
      return;
    }

    lastScannedCodeRef.current = result.data;

    await onScan?.(result);
  };

  const stopScanner = () => {
    try {
      qrScanner.current.scanner?.stop();
    } catch {}
  };

  const destroyScanner = () => {
    try {
      qrScanner.current.scanner?.destroy?.();
    } catch {}
    qrScanner.current.scanner = null;
  };

  const init = async () => {
    if (!videoRef || !onScan) {
      return;
    }
    qrScanner.current.scanner = new QrScanner(videoRef, handleScan, {
      maxScansPerSecond: 0.4,
      calculateScanRegion: () => {
        return {
          x: 0,
          y: 0,
          width: undefined,
          height: undefined,
          downScaledHeight: undefined,
          downScaledWidth: undefined
        };
      }
    });

    (qrScanner.current.scanner as any).onDecodeError = () => {};
    (qrScanner.current.scanner as any).onError = () => {};

    await qrScanner.current.scanner.start();
    const device = await initCameraDevices(deviceStorageKey);
    if (deviceStorageKey && device) {
      await qrScanner.current.scanner?.setCamera(device.id);
    }
  };

  const setDeviceIdToIndexDB = async (deviceId: string) => {
    await localForage.setItem(FREYA_DEVICE_ID_KEY, deviceId);
  };

  useEffect(() => {
    init();
  }, [videoRef]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopScanner();
      destroyScanner();
    };
  }, []);

  return {
    setCamera,
    cameraDevices,
    selectedCameraDevice,
    initCameraDevices,
    setDeviceIdToIndexDB,
    stopScanner,
    destroyScanner
  };
};
